import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
  useTheme,
  Button,
} from '@mui/material';
import { tokens } from '../theme';
import { FilterContext } from '../contexts/FilterContext';
import { dateToSqlDate } from '../helpers/dateFuncs';
import fetchData from '../helpers/fetchData';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';

interface ClientPerformance {
  client_id: number;
  client_name: string;
  lead_price: number;
  customers: string[];
  lead_count: number;
  revenue: number;
  percent_of_total: number;
}

interface DailyControllingData {
  customer: string;
  client_id: number;
  client_name: string;
  date: string;
  count: number;
}

interface ClientOption {
  client_id: number;
  client_name: string;
  lead_price: number;
  customers: string[];
}

interface TopClientsPerformanceTableProps {
  selectedCustomers: string[];
  selectedClients: number[];
}

type SortOrder = 'asc' | 'desc';
type SortColumn = 'client_name' | 'lead_count' | 'lead_price' | 'revenue' | 'percent_of_total';

const customerMapping: Record<string, string> = {
  ft: 'FT',
  bg: 'BG',
  sw: 'SW',
  tt: 'TT',
  be: 'BE',
  foreign: 'Foreign',
};

const TopClientsPerformanceTable: React.FC<TopClientsPerformanceTableProps> = ({
  selectedCustomers,
  selectedClients,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [clientPerformance, setClientPerformance] = useState<ClientPerformance[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('revenue');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  useEffect(() => {
    const fetchPerformanceData = async () => {
      if (
        !filterState.startDate ||
        !filterState.endDate ||
        selectedCustomers.length === 0 ||
        selectedClients.length === 0
      ) {
        setClientPerformance([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const options = {
          start_date: dateToSqlDate(filterState.startDate),
          end_date: dateToSqlDate(filterState.endDate),
          customers: selectedCustomers.join(','),
          clients: selectedClients.join(','),
        };

        const [leadData, clientMetadata] = await Promise.all([
          fetchData<DailyControllingData[]>(
            '/api/daily-controlling/multi-customer-client-data',
            options,
          ),
          fetchData<ClientOption[]>('/api/daily-controlling/all-clients', {}),
        ]);

        const clientMap = new Map<number, ClientPerformance>();
        const clientMetadataMap = new Map<number, ClientOption>();

        const flatMetadata = Array.isArray(clientMetadata) ? clientMetadata.flat() : [];
        flatMetadata.forEach((client) => {
          clientMetadataMap.set(client.client_id, client);
        });

        const dataArray = Array.isArray(leadData) ? leadData.flat() : [];
        dataArray.forEach((item) => {
          if (!clientMap.has(item.client_id)) {
            const metadata = clientMetadataMap.get(item.client_id);
            clientMap.set(item.client_id, {
              client_id: item.client_id,
              client_name: item.client_name,
              lead_price: metadata?.lead_price || 0,
              customers: metadata?.customers || [],
              lead_count: 0,
              revenue: 0,
              percent_of_total: 0,
            });
          }

          const performance = clientMap.get(item.client_id)!;
          performance.lead_count += item.count || 0;
          performance.revenue += (item.count || 0) * performance.lead_price;
        });

        const performanceArray = Array.from(clientMap.values());
        const totalRevenue = performanceArray.reduce((sum, p) => sum + p.revenue, 0);

        performanceArray.forEach((p) => {
          p.percent_of_total = totalRevenue > 0 ? (p.revenue / totalRevenue) * 100 : 0;
        });

        setClientPerformance(performanceArray);
      } catch (err: any) {
        console.error('Error fetching client performance data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceData();
  }, [filterState.startDate, filterState.endDate, selectedCustomers, selectedClients]);

  const sortedData = useMemo(() => {
    const sorted = [...clientPerformance];
    sorted.sort((a, b) => {
      let aValue: number | string = a[sortColumn];
      let bValue: number | string = b[sortColumn];

      if (sortColumn === 'client_name') {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
        return sortOrder === 'asc' ? (aValue < bValue ? -1 : 1) : aValue > bValue ? -1 : 1;
      }

      return sortOrder === 'asc'
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    });
    return sorted;
  }, [clientPerformance, sortColumn, sortOrder]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
  };

  const handleExport = () => {
    const exportData = sortedData.map((client) => ({
      'Client Name': client.client_name,
      'Lead Count': client.lead_count,
      'Lead Price (EUR)': client.lead_price.toFixed(2),
      'Revenue (EUR)': client.revenue.toFixed(2),
      '% of Total': client.percent_of_total.toFixed(1),
      Customers: client.customers.map((c) => customerMapping[c] || c).join(', '),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Top Clients');
    XLSX.writeFile(workbook, 'top_clients_performance.xlsx');
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (loading) {
    return (
      <Box p={2}>
        <Typography>Laden...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2} color="error.main">
        Error: {error}
      </Box>
    );
  }

  if (clientPerformance.length === 0) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">
          Keine Daten für den ausgewählten Zeitraum verfügbar.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">
          Top Clients nach Umsatz
        </Typography>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} size="small">
          Excel Export
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === 'client_name'}
                  direction={sortColumn === 'client_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('client_name')}
                >
                  Client Name
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortColumn === 'lead_count'}
                  direction={sortColumn === 'lead_count' ? sortOrder : 'asc'}
                  onClick={() => handleSort('lead_count')}
                >
                  Lead Count
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortColumn === 'lead_price'}
                  direction={sortColumn === 'lead_price' ? sortOrder : 'asc'}
                  onClick={() => handleSort('lead_price')}
                >
                  Lead Price
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortColumn === 'revenue'}
                  direction={sortColumn === 'revenue' ? sortOrder : 'asc'}
                  onClick={() => handleSort('revenue')}
                >
                  Revenue
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortColumn === 'percent_of_total'}
                  direction={sortColumn === 'percent_of_total' ? sortOrder : 'asc'}
                  onClick={() => handleSort('percent_of_total')}
                >
                  % of Total
                </TableSortLabel>
              </TableCell>
              <TableCell>Customers</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((client) => (
              <TableRow key={client.client_id} hover>
                <TableCell>{client.client_name}</TableCell>
                <TableCell align="right">{client.lead_count.toLocaleString('de-DE')}</TableCell>
                <TableCell align="right">{formatCurrency(client.lead_price)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(client.revenue)}
                </TableCell>
                <TableCell align="right">{client.percent_of_total.toFixed(1)}%</TableCell>
                <TableCell>
                  {client.customers.map((c) => customerMapping[c] || c).join(', ')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TopClientsPerformanceTable;
